import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  RefreshCw, ShieldCheck, ShieldAlert, AlertTriangle, Check, X, Radio, KeyRound, Trash2, Globe,
} from 'lucide-react';

const when = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return d.toLocaleString();
};

const TONE = {
  live: 'bg-emerald-900/50 text-emerald-200 border-emerald-700/50',
  test: 'bg-amber-900/50 text-amber-200 border-amber-700/50',
  set: 'bg-emerald-900/50 text-emerald-200 border-emerald-700/50',
  missing: 'bg-red-900/50 text-red-200 border-red-700/50',
  unrecognised: 'bg-red-900/50 text-red-200 border-red-700/50',
};

const Row = ({ label, value, note, testid }) => (
  <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-800 last:border-0">
    <div>
      <p className="text-sm text-gray-300">{label}</p>
      {note && <p className="text-xs text-gray-500 mt-0.5">{note}</p>}
    </div>
    <Badge className={`${TONE[value] || 'bg-gray-800 text-gray-300 border-gray-700'} border text-xs whitespace-nowrap`}
           data-testid={testid}>
      {value}
    </Badge>
  </div>
);

function ConnectSecretCard({ config, onSaved }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const isSet = config.connect_webhook_secret === 'set';
  const inEnv = isSet && config.connect_webhook_secret_source !== 'admin panel';

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/stripe/connect-webhook-secret`, { secret: value.trim() });
      toast.success('Connect signing secret saved. Connected-account events will verify now.');
      setValue('');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that secret');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/stripe/connect-webhook-secret`);
      toast.success('Removed.');
      onSaved();
    } catch {
      toast.error('Could not remove it');
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-gray-900/60 border border-gray-800" data-testid="stripe-connect-secret-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-teal-300" />
          <h3 className="text-base font-semibold text-white">Connect webhook signing secret</h3>
        </div>
        <p className="text-sm text-gray-400 mt-2 leading-relaxed">
          A Connect endpoint in Stripe is a separate endpoint with its own signing secret, even when
          it points at the same URL. Without it, <span className="text-gray-200">account.updated</span>{' '}
          and <span className="text-gray-200">transfer.*</span> events from practitioner accounts are
          rejected, so onboarding and payout status never update. The Secrets panel will not accept a
          new <span className="text-gray-200">STRIPE_</span> name, so paste it here instead.
        </p>

        {isSet ? (
          <div className="mt-4 flex items-center justify-between gap-3 bg-emerald-950/30 border border-emerald-800/40 rounded-lg px-4 py-3">
            <p className="text-sm text-emerald-200" data-testid="stripe-connect-secret-status">
              Saved{config.connect_webhook_secret_tail ? ` — ends …${config.connect_webhook_secret_tail}` : ''}
              <span className="text-emerald-400/70"> · from {config.connect_webhook_secret_source}</span>
            </p>
            {!inEnv && (
              <Button variant="outline" size="sm" disabled={busy} onClick={remove}
                      className="border-red-800/60 text-red-300 hover:bg-red-950/40"
                      data-testid="stripe-connect-secret-remove">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
              </Button>
            )}
          </div>
        ) : null}

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="whsec_…"
            className="bg-gray-950 border-gray-700 text-gray-100 font-mono text-sm"
            data-testid="stripe-connect-secret-input"
          />
          <Button onClick={save} disabled={busy || !value.trim()}
                  className="bg-teal-600 hover:bg-teal-500 text-white shrink-0"
                  data-testid="stripe-connect-secret-save">
            {isSet ? 'Replace secret' : 'Save secret'}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Stripe → Developers → Webhooks → the endpoint listening to <em>connected accounts</em> →
          Signing secret → Reveal.
        </p>
      </CardContent>
    </Card>
  );
}

function WebhookActivity({ webhooks }) {
  const last = webhooks.last_accepted;
  const rejected = webhooks.last_rejected;
  return (
    <Card className="bg-gray-900/60 border border-gray-800" data-testid="stripe-webhook-activity">
      <CardContent className="p-5">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-violet-300" />
          <h3 className="text-base font-semibold text-white">What Stripe has actually sent here</h3>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[
            ['Accepted · 24h', webhooks.accepted_24h, 'text-emerald-300'],
            ['Rejected · 24h', webhooks.rejected_24h, webhooks.rejected_24h ? 'text-red-300' : 'text-gray-400'],
            ['Accepted · 7d', webhooks.accepted_7d, 'text-emerald-300'],
            ['Rejected · 7d', webhooks.rejected_7d, webhooks.rejected_7d ? 'text-red-300' : 'text-gray-400'],
          ].map(([label, n, tone]) => (
            <div key={label} className="bg-gray-950/60 border border-gray-800 rounded-lg px-3 py-2">
              <p className={`text-xl font-semibold ${tone}`}>{n ?? 0}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-2 text-sm">
          <p className="text-gray-300" data-testid="stripe-last-accepted">
            {last
              ? <>Last accepted: <span className="text-white">{last.event_type}</span> · {when(last.at)} · {last.livemode ? 'live' : 'test'} mode · verified with {last.secret_used}</>
              : <span className="text-amber-300">No notification from Stripe has ever been accepted here.</span>}
          </p>
          {webhooks.last_connect_accepted && (
            <p className="text-gray-400">
              Last connected-account event: <span className="text-gray-200">{webhooks.last_connect_accepted.event_type}</span> · {when(webhooks.last_connect_accepted.at)}
            </p>
          )}
          {rejected && (
            <p className="text-red-300/90 text-xs leading-relaxed" data-testid="stripe-last-rejected">
              Last rejection {when(rejected.at)}: {rejected.detail}
            </p>
          )}
        </div>

        {webhooks.recent?.length > 0 && (
          <div className="mt-4 border-t border-gray-800 pt-3">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Recent deliveries</p>
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {webhooks.recent.map((d, i) => (
                <div key={i} className="flex items-center justify-between gap-3 text-xs">
                  <span className="flex items-center gap-2 min-w-0">
                    {d.result === 'accepted'
                      ? <Check className="w-3 h-3 text-emerald-400 shrink-0" />
                      : <X className="w-3 h-3 text-red-400 shrink-0" />}
                    <span className="text-gray-300 truncate">
                      {d.result === 'accepted' ? d.event_type : `rejected: ${d.event_type}`}
                    </span>
                    {d.livemode === false && <span className="text-amber-500/80">test</span>}
                  </span>
                  <span className="text-gray-600 shrink-0">{when(d.at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SiteUrlCard({ config, onSaved }) {
  const [value, setValue] = useState(config.site_url || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/site/public-url`, { url: value.trim() });
      toast.success('Saved. Links, emails and Stripe return addresses will use it from now on.');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that address');
    } finally { setBusy(false); }
  };

  return (
    <Card className="bg-gray-900/60 border border-gray-800" data-testid="site-url-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-sky-300" />
          <h3 className="text-base font-semibold text-white">Public site address</h3>
        </div>
        <p className="text-sm text-gray-400 mt-2 leading-relaxed">
          Everything the server writes for someone else to click is built from this: password
          resets, booking confirmations, the page Stripe returns a practitioner to after onboarding,
          and the website recorded on their Stripe account.
        </p>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="https://your-domain.com"
            className="bg-gray-950 border-gray-700 text-gray-100 text-sm"
            data-testid="site-url-input"
          />
          <Button onClick={save} disabled={busy || !value.trim() || value.trim() === config.site_url}
                  className="bg-sky-600 hover:bg-sky-500 text-white shrink-0"
                  data-testid="site-url-save">
            Save address
          </Button>
        </div>
        <p className={`text-xs mt-2 ${config.site_url_is_temporary ? 'text-amber-400' : 'text-gray-500'}`}
           data-testid="site-url-current">
          Currently using <span className="font-mono">{config.site_url || 'nothing'}</span> · from {config.site_url_source}
          {config.site_url_is_temporary && ' — this is a temporary platform address, not your domain'}
        </p>
      </CardContent>
    </Card>
  );
}

export default function AdminStripeTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/stripe/health`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read the Stripe health check');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <div className="text-gray-400 text-sm p-6" data-testid="stripe-health-loading">
        {loading ? 'Reading the live Stripe configuration…' : 'Nothing to show.'}
      </div>
    );
  }

  const c = data.configuration;
  const ok = data.ready_for_real_money;

  return (
    <div className="space-y-5" data-testid="admin-stripe-tab">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {ok ? <ShieldCheck className="w-7 h-7 text-emerald-400 mt-0.5" />
              : <ShieldAlert className="w-7 h-7 text-amber-400 mt-0.5" />}
          <div>
            <h2 className="text-xl font-semibold text-white">Stripe wiring</h2>
            <p className="text-sm text-gray-400 mt-0.5" data-testid="stripe-verdict">
              {ok
                ? 'Everything checks out — this site is ready to take real money.'
                : `${data.warnings.length} thing${data.warnings.length === 1 ? '' : 's'} still need attention before real money is safe.`}
              {' '}Running in <span className={data.mode === 'live' ? 'text-emerald-300' : 'text-amber-300'}>{data.mode}</span> mode.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                className="border-gray-700 text-gray-300 shrink-0" data-testid="stripe-health-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Re-check
        </Button>
      </div>

      {data.warnings.length > 0 && (
        <Card className="bg-amber-950/20 border border-amber-800/40" data-testid="stripe-warnings">
          <CardContent className="p-5 space-y-2.5">
            {data.warnings.map((w, i) => (
              <p key={i} className="flex gap-2.5 text-sm text-amber-100/90 leading-relaxed">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <span>{w}</span>
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="bg-gray-900/60 border border-gray-800" data-testid="stripe-config-card">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-white mb-3">Keys this server is holding</h3>
            <Row label="Secret key (charging)" value={c.secret_key} testid="cfg-secret-key" />
            <Row label="Publishable key (server copy)" value={c.publishable_key} testid="cfg-publishable-key" />
            <Row label="Publishable key the browser loads" value={c.frontend_publishable_key}
                 note="REACT_APP_STRIPE_PUBLISHABLE_KEY" testid="cfg-frontend-key" />
            <Row label="Webhook signing secret" value={c.webhook_secret} testid="cfg-webhook-secret" />
            <Row label="Connect webhook signing secret" value={c.connect_webhook_secret}
                 note={c.connect_webhook_secret === 'set' ? `from ${c.connect_webhook_secret_source}` : null}
                 testid="cfg-connect-secret" />
            <Row label="Database" value={c.database || '—'} testid="cfg-database" />
            {c.database && !c.database_confirmed && /test/i.test(c.database) && (
              <Button variant="outline" size="sm" onClick={async () => {
                try {
                  await axios.post(`${API}/admin/site/confirm-database`);
                  toast.success('Noted — the database warning is cleared.');
                  load();
                } catch { toast.error('Could not save that'); }
              }} className="mt-3 w-full border-gray-700 text-gray-300"
                      data-testid="confirm-database-name">
                This is the right database — stop warning me
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gray-900/60 border border-gray-800" data-testid="stripe-money-card">
          <CardContent className="p-5">
            <h3 className="text-base font-semibold text-white mb-3">Money at Stripe</h3>
            {data.money.balance_error ? (
              <p className="text-sm text-red-300">{data.money.balance_error}</p>
            ) : (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-gray-950/60 border border-gray-800 rounded-lg px-3 py-2">
                  <p className="text-xl font-semibold text-emerald-300">${data.money.available}</p>
                  <p className="text-[11px] text-gray-500">available to pay out</p>
                </div>
                <div className="bg-gray-950/60 border border-gray-800 rounded-lg px-3 py-2">
                  <p className="text-xl font-semibold text-gray-200">${data.money.pending}</p>
                  <p className="text-[11px] text-gray-500">still clearing</p>
                </div>
              </div>
            )}
            <Row label="Orders in this database" value={String(data.money.orders)} testid="money-orders" />
            <Row label="Payouts waiting" value={String(data.money.payouts_waiting)} testid="money-payouts-waiting" />
            <Row label="Payouts with no linked payment" value={String(data.money.payouts_unlinked)}
                 testid="money-payouts-unlinked" />
          </CardContent>
        </Card>
      </div>

      <SiteUrlCard config={c} onSaved={load} />
      <ConnectSecretCard config={c} onSaved={load} />
      {data.webhooks && <WebhookActivity webhooks={data.webhooks} />}
    </div>
  );
}
